import { Button, Space, Spin, Progress } from 'antd';
import { PlayCircleOutlined } from '@ant-design/icons';
// import { VideoPlayer } from '../../../video/player/videoPlayer';
import { useGlobalContext } from '../../../context/globalContext';
import { useEffect, useState, useMemo, memo, useCallback, useRef } from 'react';
import { millisecondsToFrames, prefetchAsset } from '@src/shared/utils/remotion';
import { addIdsToHtmlFromJson, extractPagesFromHtml } from '@src/shared/utils/remotion/htmlProcessing';
import Workspace from 'polotno/canvas/workspace';
import { PageControls } from './workspace/pageController';
import { VideoPlayer } from '@src/features/video/player/videoPlayer';
import { usePlayer } from '@src/context/playerContext';
import { ResizeDropdown } from '@src/features/resizeDropdown/resizeDropdown';
import './previewToggle.less';
import { AnimationDropdown } from '@src/features/animation/animationSelect';
import _ from 'lodash';


interface PreviewToggleProps {
  store: any;
}

// Cache for HTML conversion results
const htmlCache = new Map<string, string>();

// Utility to strip .url from data and remove ctaObject
function stripIrrelevantScriptDetails(scriptDetails: any) {
  if (!scriptDetails) return scriptDetails;
  // Deep clone to avoid mutating the original
  const clone = JSON.parse(JSON.stringify(scriptDetails));
  // Remove all .url from data
  if (Array.isArray(clone.data)) {
    clone.data = clone.data.map((item: any) => {
      if (!item || typeof item !== 'object') return item;
      const { url, ...rest } = item;
      return rest;
    });
  }
  // Remove ctaObject from cache key to prevent re-renders
  // delete clone.ctaObject;
  return clone;
}

export const PreviewToggle: React.FC<PreviewToggleProps> = ({ store }: PreviewToggleProps) => {

  const [isLoading, setIsLoading] = useState(false);
  const [previewData, setPreviewData] = useState<any>({});
  const [shouldAutoPlay, setShouldAutoPlay] = useState(false);
  const [conversionProgress, setConversionProgress] = useState(0);
  const playerRef = usePlayer();
  const [isPlayerConnected, setIsPlayerConnected] = useState(false);
  const conversionTimeoutRef = useRef<NodeJS.Timeout>();
  const lastStoreStateRef = useRef<string>('');

  const {
    TransitionAnimation: [transitionAnimation],
    ScriptDetails: [scriptDetails],
    VideoStyle: [videoStyle],
    UserDetail: [userDetails],
    ActiveView: [activeView, setActiveView],
  } = useGlobalContext();

  // Generate a cache key based on store state and scriptDetails
  const generateCacheKey = useCallback(() => {
    const storeState = JSON.stringify(store.toJSON());
    // Add scriptDetails to the cache key, but strip .url and ctaObject
    const strippedScriptDetails = stripIrrelevantScriptDetails(scriptDetails);
    return JSON.stringify({ store: storeState, scriptDetails: strippedScriptDetails });
  }, [store, scriptDetails]);

  // Optimized HTML conversion with progress tracking
  const convertToHTML = useCallback(async () => {
    const cacheKey = generateCacheKey();
    
    // Check cache first
    if (htmlCache.has(cacheKey)) {
      return htmlCache.get(cacheKey);
    }

    // Start progress tracking
    setConversionProgress(0);
    const progressInterval = setInterval(() => {
      setConversionProgress(prev => Math.min(prev + 5, 90));
    }, 100);

    try {
      const result = await store.toHTML();
      clearInterval(progressInterval);
      setConversionProgress(100);
      
      // Cache the result
      htmlCache.set(cacheKey, result);
      
      return result;
    } catch (error) {
      clearInterval(progressInterval);
      setConversionProgress(0);
      throw error;
    }
  }, [store, generateCacheKey, scriptDetails]);

  // Debounced load preview data
  const debouncedLoadPreviewData = useMemo(
    () =>
      _.debounce(async () => {
        setIsLoading(true);
        try {
          const jsonData = store.toJSON();
          const burstImagesTransitionAnimationArray = [];

          if (videoStyle === "Burst") {
            for (let i = 0; i < scriptDetails.data.length; i++) {
              const innerArray = [];
              for (let j = 0; j < scriptDetails.data[i].images.length; j++) {
                innerArray.push(transitionAnimation[0].animation);
              }
              burstImagesTransitionAnimationArray.push(innerArray);
            }
          }

          // Get HTML code with progress tracking
          const result = await convertToHTML();
          const htmlCode = extractPagesFromHtml(result);
          const htmlCodeWithTags = addIdsToHtmlFromJson(htmlCode, jsonData);

          setPreviewData({
            htmlCode: htmlCodeWithTags,
            duration: millisecondsToFrames(store.duration, 30),
            width: store.width,
            height: store.height,
            burstImageAnimations: burstImagesTransitionAnimationArray
          });
        } catch (error) {
          console.error('Error loading preview data:', error);
        } finally {
          setIsLoading(false);
          setConversionProgress(0);
        }
      }, 500),
    [store, transitionAnimation, convertToHTML]
  );

  // Effect to handle store changes
  useEffect(() => {
    const currentStoreState = generateCacheKey();
    if (currentStoreState !== lastStoreStateRef.current) {
      lastStoreStateRef.current = currentStoreState;
      debouncedLoadPreviewData();
    }
  }, [store, generateCacheKey, debouncedLoadPreviewData, scriptDetails]);

  // Effect to handle view changes
  useEffect(() => {
    if (activeView === 'preview') {
      store.pages.map((page: any) => {
        const elementID = "captionText" + page.id;
        if (store.getElementById(elementID)) {
          store.getElementById(elementID).set({
            opacity: 0,
            visible: false,
          });
        }
      });
      debouncedLoadPreviewData();
    } else {
      store.history.ignore(async () => {
        store.pages.map((page: any) => {
          if (page.custom.showSubtitles === true) {
            const elementID = "captionText" + page.id;
            if (store.getElementById(elementID)) {
              store.getElementById(elementID).set({
                opacity: 1,
                visible: true,
              });
            }
          }
        });
      });
      debouncedLoadPreviewData();
    }
  }, [activeView, store, debouncedLoadPreviewData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedLoadPreviewData.cancel();
      if (conversionTimeoutRef.current) {
        clearTimeout(conversionTimeoutRef.current);
      }
    };
  }, [debouncedLoadPreviewData]);

  // Effect to handle auto-play when switching to preview
  useEffect(() => {
    if (activeView === 'preview' && shouldAutoPlay && playerRef.current && !isLoading && isPlayerConnected) {
   
      playerRef.current.play();
      setShouldAutoPlay(false);
    }
  }, [activeView, shouldAutoPlay, playerRef, isLoading, isPlayerConnected]);

 

  const handleToggleClick = (newView: string) => {
    if (newView === 'preview') {
      setShouldAutoPlay(true);
    }else{
      playerRef.current?.pause();

    }
    setActiveView(newView);
  }

  // Memoize the video player props
  const videoPlayerProps = useMemo(() => ({
    htmlData: previewData?.htmlCode,
    duration: previewData?.duration,
    scriptDetails: scriptDetails,
    jsonData: store.toJSON(),
    transitionDurationInFrames: transitionAnimation[0].duration,
    transitionAnimationProp: transitionAnimation[0].animation,
    isDownload: false,
    burstImagesTransitionAnimationArray: previewData?.burstImageAnimations,
    videoStyle: videoStyle,
    aiAvatar: userDetails?.purpose?.aiAvatar,
    isWorkspace: true
  }), [
    previewData?.htmlCode,
    previewData?.duration,
    scriptDetails,
    store,
    transitionAnimation,
    previewData?.burstImageAnimations,
    videoStyle,
    userDetails?.purpose?.aiAvatar
  ]);

  return (
    <div className="preview-toggle">
      <div className="toggle-buttons">
        <Space>
          <Button
            type="default"
            className={activeView === 'canvas' ? 'active-toggle' : ''}
            onClick={() => handleToggleClick('canvas')}
          >
            Canvas
          </Button>
          <Button
            type="default"
            className={activeView === 'preview' ? 'active-toggle' : ''}
            onClick={() => handleToggleClick('preview')}
            icon={<PlayCircleOutlined />}
          >
            Preview
          </Button>
        </Space>
      </div>

      <div className="main-content">
        <div style={{ 
          display: activeView === 'canvas' ? 'block' : 'none',
          height: '100%'
        }}>
          <Workspace
            store={store}
            pageBorderColor='white'
            activePageBorderColor='var(--color-primary-500)'
            backgroundColor='var(--color-canvas)'
            components={{ PageControls }}
            renderOnlyActivePage={true}
          />
        </div>
        <div style={{ 
          display: activeView === 'preview' ? 'block' : 'none',
          height: '100%'
        }}>
          {isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Spin size="large" />
              {conversionProgress > 0 && (
                <Progress 
                  percent={conversionProgress} 
                  size="small" 
                  style={{ marginTop: 16, width: 200 }}
                  status={conversionProgress === 100 ? 'success' : 'active'}
                />
              )}
            </div>
          ) : (
            <>
              {previewData?.duration ? (
                <div className="video-container" style={{
                  width: store.width*0.6,
                  height: store.height*0.6
                }}>
                  <div className="video-player-wrapper">
                    <VideoPlayer {...videoPlayerProps} />
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Spin size="large" />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="bottom-controls">
        <ResizeDropdown store={store} />
        <AnimationDropdown store={store} />
      </div>
    </div>
  );
};

// Memoize the VideoPlayer component
const MemoizedVideoPlayer = memo(VideoPlayer, (prevProps, nextProps) => {
  // Custom comparison function to determine if re-render is needed
  return (
    prevProps.htmlData === nextProps.htmlData &&
    prevProps.duration === nextProps.duration &&
    prevProps.scriptDetails === nextProps.scriptDetails &&
    JSON.stringify(prevProps.scriptDetails?.ctaObject) === JSON.stringify(nextProps.scriptDetails?.ctaObject) &&
    prevProps.jsonData === nextProps.jsonData &&
    prevProps.transitionDurationInFrames === nextProps.transitionDurationInFrames &&
    prevProps.transitionAnimationProp === nextProps.transitionAnimationProp &&
    prevProps.isDownload === nextProps.isDownload &&
    prevProps.burstImagesTransitionAnimationArray === nextProps.burstImagesTransitionAnimationArray &&
    prevProps.videoStyle === nextProps.videoStyle &&
    prevProps.aiAvatar === nextProps.aiAvatar &&
    prevProps.isWorkspace === nextProps.isWorkspace
  );
});

// Export the memoized component
export default PreviewToggle; 